import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Order, orderService } from '../services/orderService';

const PaymentInstructions = ({ paymentMethod, customerName }: { paymentMethod: string, customerName: string }) => {
  switch (paymentMethod) {
    case 'paypal':
      return (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Veuillez effectuer votre paiement via PayPal à l'adresse suivante :{' '}
                <span className="font-semibold">@3Bpaul</span>
                <br />
                Mention à indiquer : <span className="font-semibold">{customerName} - Mine des mines</span>
              </p>
            </div>
          </div>
        </div>
      );
    case 'rib':
      return (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Veuillez effectuer votre virement aux coordonnées suivantes :
                <br />
                <span className="font-semibold">IBAN : FR76 4061 8804 2100 0403 6688 339</span>
                <br />
                Mention à indiquer : <span className="font-semibold">{customerName} - Mine des mines</span>
              </p>
            </div>
          </div>
        </div>
      );
    case 'cash':
      return (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                Veuillez vous adresser à votre MST pour effectuer le paiement en espèces.
              </p>
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
};

export const OrderConfirmation = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) {
        setError('ID de commande manquant');
        setLoading(false);
        return;
      }

      try {
        console.log('Récupération de la commande avec l\'ID:', id);
        const foundOrder = await orderService.getOrder(id);
        if (!foundOrder) {
          setError('Commande non trouvée');
          setLoading(false);
          return;
        }
        
        console.log('Commande trouvée:', foundOrder);
        setOrder(foundOrder);
      } catch (err) {
        console.error('Erreur lors de la récupération de la commande:', err);
        setError('Une erreur est survenue lors de la récupération de la commande');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de la commande...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/store')}
            className="mt-4 px-6 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
          >
            Retour à la boutique
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const customerName = `${order.customer_info.firstName} ${order.customer_info.lastName}`;
  const orderDate = new Date(order.date);
  const expirationDate = new Date(order.expiration_date);
  const cancellationDate = new Date(order.cancellation_date);

  return (
    <div className="pt-16 min-h-screen bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold mb-8">Confirmation de commande</h1>
        
        <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Détails de la commande</h2>
            <p className="text-gray-600">Numéro de commande: {order.id}</p>
            <p className="text-gray-600">Date: {orderDate.toLocaleDateString()}</p>
            <p className="text-gray-600">Statut: {order.status}</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Articles commandés</h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-gray-600">{item.quantity} x {item.price}€</p>
                  </div>
                  <p className="font-medium">{(item.price * item.quantity).toFixed(2)}€</p>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-4 mb-6">
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span>{order.total.toFixed(2)}€</span>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Instructions de paiement</h2>
            <PaymentInstructions 
              paymentMethod={order.payment_method} 
              customerName={customerName}
            />
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-orange-700">
                  <span className="font-semibold">Important :</span> Votre commande est valable jusqu'au {cancellationDate.toLocaleDateString()}.
                  <br />
                  Si le paiement n'est pas effectué avant le {cancellationDate.toLocaleDateString()}, la commande sera automatiquement annulée.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={() => navigate('/store')}
            className="px-6 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
          >
            Retour à la boutique
          </button>
        </div>
      </div>
    </div>
  );
}; 